import { useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatInt, formatRub } from "../charts/format.js";

export interface DailyPoint {
  day: string;
  value: number | null;
}

export interface ComboTrendChartProps {
  costSeries: DailyPoint[];
  revenueSeries: DailyPoint[];
  visitsSeries: DailyPoint[];
  ordersSeries: DailyPoint[];
  roasSeries?: DailyPoint[]; // net-ROAS — на скрытой оси (ratio), линия тренда поверх
  title?: string;
  height?: number;
}

interface MergedRow {
  day: string;
  cost: number | null;
  revenue: number | null;
  visits: number | null;
  orders: number | null;
  roas: number | null;
  cost_trend?: number | null;
  revenue_trend?: number | null;
  visits_trend?: number | null;
  orders_trend?: number | null;
}

function mergeSeries(
  cost: DailyPoint[],
  revenue: DailyPoint[],
  visits: DailyPoint[],
  orders: DailyPoint[],
  roas: DailyPoint[],
): MergedRow[] {
  const days = new Set<string>();
  for (const p of cost) days.add(p.day);
  for (const p of revenue) days.add(p.day);
  for (const p of visits) days.add(p.day);
  for (const p of orders) days.add(p.day);
  for (const p of roas) days.add(p.day);

  const costMap = new Map(cost.map((p) => [p.day, p.value]));
  const revenueMap = new Map(revenue.map((p) => [p.day, p.value]));
  const visitsMap = new Map(visits.map((p) => [p.day, p.value]));
  const ordersMap = new Map(orders.map((p) => [p.day, p.value]));
  const roasMap = new Map(roas.map((p) => [p.day, p.value]));

  return Array.from(days)
    .sort()
    .map((day) => ({
      day,
      cost: costMap.get(day) ?? null,
      revenue: revenueMap.get(day) ?? null,
      visits: visitsMap.get(day) ?? null,
      orders: ordersMap.get(day) ?? null,
      roas: roasMap.get(day) ?? null,
    }));
}

function formatDayShort(iso: string): string {
  const months = ["янв","фев","мар","апр","май","июн","июл","авг","сен","окт","ноя","дек"];
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])} ${months[Number(m[2]) - 1] ?? ""}`;
}

// Линейная регрессия (least squares) для тренд-линии. Возвращает массив того же
// размера что input, с trend-y на каждом x. Null значения пропускаются.
function linearTrend(values: Array<number | null>): Array<number | null> {
  const xy: Array<{ x: number; y: number }> = [];
  values.forEach((v, i) => {
    if (v != null && Number.isFinite(v)) xy.push({ x: i, y: v });
  });
  if (xy.length < 2) return values.map(() => null);
  const n = xy.length;
  const sumX = xy.reduce((s, p) => s + p.x, 0);
  const sumY = xy.reduce((s, p) => s + p.y, 0);
  const sumXY = xy.reduce((s, p) => s + p.x * p.y, 0);
  const sumXX = xy.reduce((s, p) => s + p.x * p.x, 0);
  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return values.map(() => null);
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return values.map((_, i) => slope * i + intercept);
}

function median(values: Array<number | null>): number | null {
  const clean = values.filter((v): v is number => v != null && Number.isFinite(v));
  if (clean.length === 0) return null;
  clean.sort((a, b) => a - b);
  const mid = Math.floor(clean.length / 2);
  return clean.length % 2 === 0 ? (clean[mid - 1] + clean[mid]) / 2 : clean[mid];
}

// === Палитра ===
// Затраты — светло-розовые столбики (фон, не доминируют)
// Заказы ₽ (выручка) — бирюзовая линия на той же оси ₽ (можно сравнить расход и приход)
// Посетители — оранжевая линия (на правой оси, count)
// Заказы шт — зелёная линия (на крайней правой оси, count)
const COLOR_COST = "#F9A8D4";    // pink-300 — светло-розовый для столбцов
const COLOR_COST_AXIS = "#475569"; // slate-600 — нейтральная ось (на ней и cost, и revenue)
const COLOR_REVENUE = "#0891B2"; // cyan-600 — выручка
const COLOR_VISITS = "#EA580C";  // orange-600
const COLOR_ORDERS = "#16A34A";  // green-600
const COLOR_ROAS = "#8B5CF6";    // violet-500 — net-ROAS (скрытая ось, ratio)
const COLOR_COST_TREND = "#EC4899";    // pink-500 для тренда затрат
const COLOR_REVENUE_TREND = "#06B6D4"; // cyan-500
const COLOR_VISITS_TREND = "#FB923C";  // orange-400
const COLOR_ORDERS_TREND = "#22C55E";  // green-500

const toggleBase = {
  background: "var(--surface-card)",
  border: "1px solid var(--border-default)",
  borderRadius: 999,
  padding: "4px 12px",
  fontSize: 11,
  cursor: "pointer",
  fontFamily: "inherit",
  color: "var(--text-secondary)",
} as const;

const toggleActive = {
  ...toggleBase,
  background: "var(--text-primary)",
  color: "var(--text-inverse, #fff)",
  borderColor: "var(--text-primary)",
} as const;

export function ComboTrendChart({
  costSeries,
  revenueSeries,
  visitsSeries,
  ordersSeries,
  roasSeries,
  title = "Затраты · Заказы ₽ · Посетители · Заказы шт · net-ROAS",
  height = 320,
}: ComboTrendChartProps) {
  const [showTrend, setShowTrend] = useState(false);
  const [showMedian, setShowMedian] = useState(false);

  const data = useMemo(
    () => mergeSeries(costSeries, revenueSeries, visitsSeries, ordersSeries, roasSeries ?? []),
    [costSeries, revenueSeries, visitsSeries, ordersSeries, roasSeries],
  );

  const enrichedData = useMemo(() => {
    if (!showTrend) return data;
    const costTrend = linearTrend(data.map((d) => d.cost));
    const revenueTrend = linearTrend(data.map((d) => d.revenue));
    const visitsTrend = linearTrend(data.map((d) => d.visits));
    const ordersTrend = linearTrend(data.map((d) => d.orders));
    return data.map((d, i) => ({
      ...d,
      cost_trend: costTrend[i],
      revenue_trend: revenueTrend[i],
      visits_trend: visitsTrend[i],
      orders_trend: ordersTrend[i],
    }));
  }, [data, showTrend]);

  const medians = useMemo(
    () => ({
      cost: median(data.map((d) => d.cost)),
      revenue: median(data.map((d) => d.revenue)),
      visits: median(data.map((d) => d.visits)),
      orders: median(data.map((d) => d.orders)),
    }),
    [data],
  );

  if (data.length < 2) {
    return (
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-default)",
          borderRadius: 12,
          padding: 24,
          color: "var(--text-muted)",
          fontSize: 13,
          textAlign: "center",
        }}
      >
        Недостаточно данных для тренда
      </div>
    );
  }

  return (
    <div
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-default)",
        borderRadius: 12,
        padding: "20px 24px",
        boxShadow: "var(--shadow-card)",
        marginBottom: 24,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 12 }}>
        <div>
          <h3
            style={{
              fontSize: 13,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "var(--text-secondary)",
              margin: "0 0 4px 0",
            }}
          >
            {title}
          </h3>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            ₽ слева (затраты + заказы ₽) · посетители справа · заказы шт — справа со смещением
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button
            onClick={() => setShowTrend((v) => !v)}
            style={showTrend ? toggleActive : toggleBase}
            title="Линейная регрессия по каждой серии"
          >
            📈 Тренды
          </button>
          <button
            onClick={() => setShowMedian((v) => !v)}
            style={showMedian ? toggleActive : toggleBase}
            title="Медианные горизонтальные линии"
          >
            ⊝ Медианы
          </button>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={enrichedData} margin={{ top: 8, right: 70, bottom: 4, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default, #E2E8F0)" vertical={false} />
          <XAxis
            dataKey="day"
            tickFormatter={formatDayShort}
            tick={{ fontSize: 11, fill: "var(--text-secondary, #475569)" }}
            stroke="var(--border-default, #E2E8F0)"
            interval="preserveStartEnd"
            minTickGap={40}
          />
          {/* Левая ось — затраты в ₽ (текст темнее заливки бара для читаемости) */}
          <YAxis
            yAxisId="cost"
            orientation="left"
            tickFormatter={(v) => formatRub(v as number)}
            tick={{ fontSize: 11, fill: COLOR_COST_AXIS, fontWeight: 600 }}
            stroke={COLOR_COST_AXIS}
            width={75}
          />
          {/* Правая ось — посетители (count) */}
          <YAxis
            yAxisId="visits"
            orientation="right"
            tickFormatter={(v) => formatInt(v as number)}
            tick={{ fontSize: 11, fill: COLOR_VISITS, fontWeight: 600 }}
            stroke={COLOR_VISITS}
            width={50}
          />
          {/* Третья ось — заказы (count, со смещением) */}
          <YAxis
            yAxisId="orders"
            orientation="right"
            tickFormatter={(v) => formatInt(v as number)}
            tick={{ fontSize: 11, fill: COLOR_ORDERS, fontWeight: 600 }}
            stroke={COLOR_ORDERS}
            width={40}
          />
          {/* Скрытая ось — net-ROAS (ratio): линия видна, ось не загромождает */}
          <YAxis yAxisId="roas" orientation="right" domain={[0, "auto"]} hide />
          <Tooltip
            contentStyle={{
              background: "var(--surface-card, #fff)",
              border: "1px solid var(--border-default, #E2E8F0)",
              borderRadius: 6,
              fontSize: 12,
              fontFamily: "inherit",
            }}
            labelFormatter={(label) => formatDayShort(label as string)}
            formatter={(value, name) => {
              const n = String(name ?? "");
              if (n.includes("ROAS")) {
                return [(value as number).toLocaleString("ru-RU", { maximumFractionDigits: 2 }) + "×", n];
              }
              if (n.includes("Затраты") || n.includes("Заказы ₽") || n.includes("Выручка")) {
                return [formatRub(value as number), n];
              }
              return [formatInt(value as number), n];
            }}
            cursor={{ stroke: "var(--text-secondary, #475569)", strokeWidth: 1, strokeDasharray: "3 3" }}
          />
          <Legend
            wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
            iconType="line"
          />

          {/* Медианные линии */}
          {showMedian && medians.cost != null && (
            <ReferenceLine
              yAxisId="cost"
              y={medians.cost}
              stroke="#DB2777"
              strokeDasharray="2 4"
              strokeOpacity={0.5}
              label={{
                value: `med затрат ${formatRub(medians.cost)}`,
                position: "insideTopLeft",
                fill: "#DB2777",
                fontSize: 10,
              }}
            />
          )}
          {showMedian && medians.revenue != null && (
            <ReferenceLine
              yAxisId="cost"
              y={medians.revenue}
              stroke={COLOR_REVENUE}
              strokeDasharray="2 4"
              strokeOpacity={0.5}
              label={{
                value: `med заказов ${formatRub(medians.revenue)}`,
                position: "insideBottomLeft",
                fill: COLOR_REVENUE,
                fontSize: 10,
              }}
            />
          )}
          {showMedian && medians.visits != null && (
            <ReferenceLine
              yAxisId="visits"
              y={medians.visits}
              stroke={COLOR_VISITS}
              strokeDasharray="2 4"
              strokeOpacity={0.5}
              label={{
                value: `med ${formatInt(medians.visits)}`,
                position: "insideTopRight",
                fill: COLOR_VISITS,
                fontSize: 10,
              }}
            />
          )}

          {/* Основные серии */}
          <Bar
            yAxisId="cost"
            dataKey="cost"
            name="Затраты"
            fill={COLOR_COST}
            opacity={0.9}
            isAnimationActive={false}
          />
          <Line
            yAxisId="cost"
            type="monotone"
            dataKey="revenue"
            name="Заказы ₽"
            stroke={COLOR_REVENUE}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4, fill: "var(--surface-card, #fff)", stroke: COLOR_REVENUE, strokeWidth: 2 }}
            isAnimationActive={false}
          />
          <Line
            yAxisId="visits"
            type="monotone"
            dataKey="visits"
            name="Посетители"
            stroke={COLOR_VISITS}
            strokeWidth={2.5}
            dot={false}
            activeDot={{ r: 4, fill: "var(--surface-card, #fff)", stroke: COLOR_VISITS, strokeWidth: 2 }}
            isAnimationActive={false}
          />
          <Line
            yAxisId="orders"
            type="monotone"
            dataKey="orders"
            name="Заказы"
            stroke={COLOR_ORDERS}
            strokeWidth={2.5}
            dot={{ r: 3, fill: COLOR_ORDERS }}
            activeDot={{ r: 5, fill: "var(--surface-card, #fff)", stroke: COLOR_ORDERS, strokeWidth: 2 }}
            isAnimationActive={false}
          />
          <Line
            yAxisId="roas"
            type="monotone"
            dataKey="roas"
            name="net-ROAS"
            stroke={COLOR_ROAS}
            strokeWidth={2}
            strokeDasharray="5 3"
            dot={false}
            activeDot={{ r: 4, fill: "var(--surface-card, #fff)", stroke: COLOR_ROAS, strokeWidth: 2 }}
            connectNulls
            isAnimationActive={false}
          />

          {/* Тренд-линии (linear regression) */}
          {showTrend && (
            <>
              <Line
                yAxisId="cost"
                type="linear"
                dataKey="cost_trend"
                name="Тренд затрат"
                stroke={COLOR_COST_TREND}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
                legendType="none"
              />
              <Line
                yAxisId="cost"
                type="linear"
                dataKey="revenue_trend"
                name="Тренд заказов ₽"
                stroke={COLOR_REVENUE_TREND}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
                legendType="none"
              />
              <Line
                yAxisId="visits"
                type="linear"
                dataKey="visits_trend"
                name="Тренд посетителей"
                stroke={COLOR_VISITS_TREND}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
                legendType="none"
              />
              <Line
                yAxisId="orders"
                type="linear"
                dataKey="orders_trend"
                name="Тренд заказов"
                stroke={COLOR_ORDERS_TREND}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                activeDot={false}
                isAnimationActive={false}
                legendType="none"
              />
            </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
